import { useState, useEffect, useCallback } from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { AlertTriangle, Search, Loader2, RefreshCw, ChevronLeft, ChevronRight, ShieldAlert, Tag, Trash2, Package } from 'lucide-react';
import api from '../lib/api';
import { fmtCurrency } from '../lib/localeUtils';

const STATUS_CONFIG = {
  expired:  { label: 'Expired',  className: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300' },
  critical: { label: 'Critical', className: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300' },
  warning:  { label: 'Warning',  className: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300' },
  watch:    { label: 'Watch',    className: 'bg-sky-100 text-sky-800 dark:bg-sky-900/30 dark:text-sky-300' },
};

const ACTIONS = [
  { value: 'discount_flag', label: 'Flag for Discount', icon: Tag, desc: 'Mark as near-expiry discount item' },
  { value: 'quarantine', label: 'Quarantine Batch', icon: ShieldAlert, desc: 'Prevent from being dispensed' },
  { value: 'write_off', label: 'Write Off', icon: Trash2, desc: 'Remove stock (logs adjustment)' },
];

export default function NearExpiryPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [days, setDays] = useState('90');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [bulkAction, setBulkAction] = useState('');
  const [applying, setApplying] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/inventory/near-expiry', { params: { days: parseInt(days), page, limit: 50 } });
      setItems(res.data.items || []);
      setTotal(res.data.total || 0);
      setPages(res.data.pages || 1);
    } catch { toast.error('Failed to load near-expiry data'); }
    finally { setLoading(false); }
  }, [days, page]);

  useEffect(() => { load(); }, [load]);

  const toggleSelect = (productId) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(productId) ? next.delete(productId) : next.add(productId);
      return next;
    });
  };

  const toggleAll = () => {
    setSelected(prev => prev.size === filtered.length ? new Set() : new Set(filtered.map(it => it.product_id)));
  };

  const applyBulkAction = async () => {
    if (!bulkAction) { toast.error('Select an action first'); return; }
    if (!selected.size) { toast.error('Select at least one product'); return; }
    setApplying(true);
    try {
      const res = await api.post('/inventory/near-expiry/bulk-action', {
        product_ids: [...selected],
        action: bulkAction,
        note: `Bulk ${bulkAction} via Near-Expiry Management`,
      });
      const successCount = res.data.results?.filter(r => r.ok).length || 0;
      toast.success(`${bulkAction.replace(/_/g, ' ')} applied to ${successCount} product(s)`);
      setSelected(new Set());
      setBulkAction('');
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Action failed');
    } finally { setApplying(false); }
  };

  const filtered = search
    ? items.filter(it => it.name?.toLowerCase().includes(search.toLowerCase()) || it.category?.toLowerCase().includes(search.toLowerCase()))
    : items;

  const worstStatus = (batches) => {
    const order = ['expired', 'critical', 'warning', 'watch'];
    for (const s of order) {
      if (batches.some(b => b.status === s)) return s;
    }
    return 'watch';
  };

  return (
    <div className="space-y-6 p-6" data-testid="near-expiry-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <AlertTriangle className="w-6 h-6 text-amber-500" /> Near-Expiry Batch Management
          </h1>
          <p className="text-sm text-muted-foreground">Monitor and act on products expiring soon.</p>
        </div>
        <Button variant="outline" size="sm" onClick={load} data-testid="near-expiry-refresh"><RefreshCw className="w-4 h-4" /></Button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input placeholder="Search products…" value={search} onChange={e => setSearch(e.target.value)} className="pl-9 w-52" data-testid="near-expiry-search" />
        </div>
        <Select value={days} onValueChange={v => { setDays(v); setPage(1); }}>
          <SelectTrigger className="w-44" data-testid="near-expiry-days">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="30">Expiring in 30 days</SelectItem>
            <SelectItem value="60">Expiring in 60 days</SelectItem>
            <SelectItem value="90">Expiring in 90 days</SelectItem>
            <SelectItem value="180">Expiring in 180 days</SelectItem>
          </SelectContent>
        </Select>
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          {Object.entries(STATUS_CONFIG).map(([k, v]) => (
            <Badge key={k} className={`${v.className} text-xs`}>{v.label}</Badge>
          ))}
        </div>
      </div>

      {/* Bulk actions bar */}
      {selected.size > 0 && (
        <Card className="bg-primary/5 border-primary/30">
          <CardContent className="p-3 flex flex-wrap items-center gap-3">
            <span className="text-sm font-medium">{selected.size} product{selected.size !== 1 ? 's' : ''} selected</span>
            <Select value={bulkAction} onValueChange={setBulkAction}>
              <SelectTrigger className="w-52" data-testid="bulk-action-select"><SelectValue placeholder="Choose action…" /></SelectTrigger>
              <SelectContent>
                {ACTIONS.map(a => <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button onClick={applyBulkAction} disabled={applying || !bulkAction} size="sm" data-testid="bulk-action-apply">
              {applying ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}Apply
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setSelected(new Set())}>Clear</Button>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            {total} product{total !== 1 ? 's' : ''} with near-expiry batches
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10">
                    <Checkbox checked={filtered.length > 0 && selected.size === filtered.length} onCheckedChange={toggleAll} data-testid="select-all-near-expiry" />
                  </TableHead>
                  <TableHead>Product</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Batches Expiring</TableHead>
                  <TableHead>Worst Status</TableHead>
                  <TableHead>Total Qty</TableHead>
                  <TableHead>Value at Risk</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.length === 0 && (
                  <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                    <Package className="w-8 h-8 mx-auto mb-2 opacity-40" />
                    No near-expiry batches found in the next {days} days
                  </TableCell></TableRow>
                )}
                {filtered.map(item => {
                  const batches = item.near_expiry_batches || [];
                  const worst = worstStatus(batches);
                  const totalQty = batches.reduce((s, b) => s + (b.quantity || 0), 0);
                  const valueAtRisk = totalQty * (item.selling_price || 0);
                  return (
                    <TableRow key={item.product_id} data-testid={`near-expiry-row-${item.product_id}`}
                      className={selected.has(item.product_id) ? 'bg-primary/5' : ''}>
                      <TableCell>
                        <Checkbox checked={selected.has(item.product_id)} onCheckedChange={() => toggleSelect(item.product_id)} data-testid={`select-${item.product_id}`} />
                      </TableCell>
                      <TableCell className="font-medium">{item.name}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{item.category || '—'}</TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          {batches.map((b, i) => (
                            <div key={i} className="text-xs flex items-center gap-2">
                              <span className="font-mono">{b.batch_number || 'No batch'}</span>
                              <span className="text-muted-foreground">·</span>
                              <span>{b.expiry_date || 'No date'}</span>
                              <span className="text-muted-foreground">·</span>
                              <span>{b.quantity} {item.unit || 'units'}</span>
                            </div>
                          ))}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={STATUS_CONFIG[worst]?.className || ''} data-testid={`expiry-status-${item.product_id}`}>
                          {STATUS_CONFIG[worst]?.label}
                        </Badge>
                      </TableCell>
                      <TableCell>{totalQty} {item.unit || 'units'}</TableCell>
                      <TableCell className={worst === 'expired' ? 'text-red-600 font-medium' : ''}>
                        {fmtCurrency(valueAtRisk)}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
          {pages > 1 && (
            <div className="flex items-center justify-center gap-3 p-4 border-t">
              <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(p => p - 1)}><ChevronLeft className="w-4 h-4" /></Button>
              <span className="text-sm">Page {page} of {pages}</span>
              <Button size="sm" variant="outline" disabled={page >= pages} onClick={() => setPage(p => p + 1)}><ChevronRight className="w-4 h-4" /></Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
